import { dotSyntaxResolver, hasError } from '@/common/dotSyntaxResolver';
import TooltipError from '@/presentation/tooltipError';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { SCheckbox, SLabel, SWrapper } from './styles/LabeledCheckbox.styled';
import useKeyPressHandler from '@/hooks/useKeyPressHandler';

interface IProps {
  name: string;
  setIsChecked?: (check: boolean) => void;
  label: string;
  isDisabled?: boolean;
  checked?: boolean;
  isFormControl?: boolean;
}

const LabeledCheckbox: React.FC<IProps> = ({
  name,
  label,
  setIsChecked,
  isDisabled,
  isFormControl = true,
  checked = false,
}) => {
  const id = label.replace(/ /g, '');

  if (isFormControl) {
    const {
      control,
      formState: { errors },
    } = useFormContext();

    const validateCheckbox = () => {
      return (errors[name] !== null && errors[name] !== undefined) || hasError(name, errors, false);
    };

    return (
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value } }) => {
          const clickHandler = () => {
            if (!isDisabled) {
              if (setIsChecked) setIsChecked(!value);
              onChange(!value);
            }
          };
          const { onKeyPressHandler } = useKeyPressHandler(clickHandler);

          return (
            <SWrapper
              hasError={validateCheckbox()}
              isDisabled={isDisabled ?? false}
              className="hoverable"
              onClick={clickHandler}
              onKeyUp={onKeyPressHandler}
            >
              <SCheckbox
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (!isDisabled) {
                      if (setIsChecked) setIsChecked(!value);
                      onChange(!value);
                    }
                  }
                }}
                id={`cbx${id}`}
                isChecked={value}
                hasError={validateCheckbox()}
              >
                <FontAwesomeIcon icon={faCheck} />
              </SCheckbox>
              <SLabel id={`lbl${id}`} isChecked={value}>
                {label}
              </SLabel>
              {hasError(name, errors, false) && (
                <TooltipError absolutePos={false} message={dotSyntaxResolver(name, errors).message} />
              )}
            </SWrapper>
          );
        }}
      />
    );
  } else {
    const clickHandler = () => {
      if (!isDisabled && setIsChecked) setIsChecked(!checked);
    };
    const { onKeyPressHandler } = useKeyPressHandler(clickHandler);

    return (
      <SWrapper
        isDisabled={isDisabled ?? false}
        className="hoverable"
        onClick={clickHandler}
        onKeyUp={onKeyPressHandler}
      >
        <SCheckbox id={`cbx${id}`} isChecked={checked} tabIndex={0}>
          {checked && <FontAwesomeIcon icon={faCheck} />}
        </SCheckbox>
        <SLabel id={`lbl${id}`} isChecked={checked}>
          {label}
        </SLabel>
      </SWrapper>
    );
  }
};

export default LabeledCheckbox;
